import { useEffect, useState } from "react";
import * as S from "./styles";

import { CircularProgress, Container, Grid } from "@material-ui/core";
import { withRouter } from "react-router-dom";
import CarouselComponent from "../Carousel";

import CheckIcon from "@material-ui/icons/Check";
import GradeIcon from "@material-ui/icons/Grade";
import PlaceIcon from "@material-ui/icons/Place";

import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import MultipleRoomSelector from "../MultipleRoomSelector";
import Room from "../Room";

import HotelRepository from "../../repositories/hotelRepository";
import { getSessionStorage } from "../../utils/getSessionStorage";

function HotelRoomDetails({ searchState, buscaAtual }) {
  const [hotel, setHotel] = useState();
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [roomError, setRoomError] = useState(false);
  const [boardTypeFilter, setBoardTypeFilter] = useState("all");

  const nights =
    (new Date(hotel?.availability?.end) -
      new Date(hotel?.availability?.start)) /
    (1000 * 3600 * 24);

  useEffect(() => {
    const savedHotel = getSessionStorage("@hotelSelected");
    setHotel(savedHotel);

    if (savedHotel && searchState) {
      fetchHotelRooms(savedHotel);
    }
  }, []);

  const fetchHotelRooms = async (savedHotel) => {
    setLoadingRooms(true);
    setRoomError(false);

    const hotelRepository = new HotelRepository();

    const occupancy = searchState.apartments
      ? searchState.apartments.map((apt) => ({
          adults: apt.adults || 0,
          children: apt.childs || apt.children || 0,
          childrenAges: apt.childsAge || apt.childrenAges || [],
        }))
      : [
          {
            adults: searchState.qtAdult || 1,
            children: searchState.qtChild || 0,
            childrenAges: searchState.childAge || [],
          },
        ];

    const hotelId =
      savedHotel?._originalData?.hotel?.id ||
      savedHotel?._originalData?.hotel?.details?.id ||
      savedHotel?.basicPropertyInfo?.hotelRef?.hotelCode;

    const searchData = {
      start: format(new Date(searchState.datas.startDate), "yyyy-MM-dd"),
      end: format(new Date(searchState.datas.endDate), "yyyy-MM-dd"),
      hotel: { id: hotelId },
      occupancy: occupancy,
    };

    try {
      const res = await hotelRepository.searchByHotel(searchData);

      const hotelAvails = res.data?.hotelAvails || [];

      if (res.status === 204 || !res.data || hotelAvails.length === 0) {
        setRoomError(true);
        setLoadingRooms(false);
        return;
      }

      const hotelAvail = hotelAvails[0];

      const detailRooms = hotelAvail.hotel?.details?.rooms || [];

      const mappedRoomTypes = (hotelAvail.roomGroups || []).flatMap(
        (rg, rgIndex) =>
          (rg.rooms || []).map((room) => {
            const detailRoom = detailRooms.find(
              (dr) => dr.code === room.roomType?.code,
            );
            const roomImages =
              room.images && room.images.length > 0
                ? room.images
                : detailRoom?.images || [];
            const roomFacilities =
              room.facilities && room.facilities.length > 0
                ? room.facilities
                : (detailRoom?.facilities || []).flatMap(
                    (fg) => fg.items || [],
                  );
            const roomDescription =
              room.roomType?.description || detailRoom?.description || "";

            return {
              roomName: room.roomType?.name || detailRoom?.category,
              roomCode: room.roomType?.code,
              roomDescription: roomDescription,
              roomKey: room.key,
              roomID: room.key,
              refToken: room.refToken,
              key: room.key,
              roomRates: [
                {
                  total: {
                    currencyCode: room.fares?.[0]?.price?.currency || "BRL",
                    amountAfterTax:
                      room.cashPrice || room.fares?.[0]?.price?.amount,
                  },
                  origTotal: {
                    currencyCode: room.fares?.[0]?.price?.currency || "BRL",
                    amountAfterTax:
                      room.cashPrice || room.fares?.[0]?.price?.amount,
                  },
                  ratePlan: {
                    ratePlanName: room.fares?.[0]?.description,
                  },
                  ratePlans: {
                    mealPlanInfo: {
                      mealPlanDescription: room.boardType?.name || "",
                    },
                    cancelPenalties: (room.cancellationPolicies?.penalties || []).map((penalty) => ({
                      penaltyDescription: {
                        name: penalty?.name || "",
                        description: penalty?.description || "",
                      },
                    })),
                    cancellationPolicies: room.cancellationPolicies,
                    mealsIncluded: (() => {
                      const code = (room.boardType?.code || "").toLowerCase();
                      const name = (room.boardType?.name || "").toLowerCase();
                      const isBreakfast = code.includes("breakfast") || code === "bb" || name.includes("café") || name.includes("breakfast");
                      const isHalfBoard = code === "hb" || name.includes("meia pensão") || name.includes("half board");
                      const isFullBoard = code === "fb" || name.includes("pensão completa") || name.includes("full board");
                      const isAllInclusive = code === "ai" || name.includes("all inclusive");
                      return {
                        breakfast: isBreakfast || isHalfBoard || isFullBoard || isAllInclusive,
                        lunch: isFullBoard || isAllInclusive,
                        dinner: isHalfBoard || isFullBoard || isAllInclusive,
                      };
                    })(),
                  },
                  cancellation: room?.cancellationPolicies?.penalties || [],
                  cancellationPolicies: room?.cancellationPolicies,
                  mealPlan: room.boardType?.name,
                  description: roomDescription,
                  roomStayCandidateRph: rgIndex,
                  refToken: room.refToken,
                  key: room.key,
                },
              ],
              pointsPrice: room.pointsPrice,
              cashPrice: room.cashPrice,
              guestRoom: {
                amenitiesType: {
                  roomAmenities: roomFacilities.map((f) => ({
                    roomAmenity: f.name,
                  })),
                },
                multimediaDescriptionsType: {
                  multimediaDescriptions: [
                    {},
                    {
                      imageItemsType: {
                        imageItems: roomImages.map((img) => ({
                          url: {
                            address: img.large || img.medium || img.small,
                          },
                        })),
                      },
                    },
                  ],
                },
              },
            };
          }),
      );

      const hotelData = hotelAvail.hotel || {};
      const details = hotelData.details || {};

      // Map facilities from detail response
      const rawFacilities =
        details.facilities ||
        hotelData.facilities ||
        [];
      const hotelFacilities = rawFacilities.flatMap((f) => {
        if (f.items && f.items.length > 0) {
          return f.items.map((item) => ({ hotelAmenity: item.name || item }));
        }
        if (f.name) {
          return [{ hotelAmenity: f.name }];
        }
        return [];
      });

      // Get description from detail response
      const hotelDescription =
        details.description ||
        details.shortDescription ||
        details.longDescription ||
        details.overview ||
        hotelData.description ||
        "";

      // Get check-in/check-out times from detail response
      const checkInTime = details.checkInTime || hotelData.checkInTime;
      const checkOutTime = details.checkOutTime || hotelData.checkOutTime;

      // Get images from detail response
      const detailImages = (details.images || hotelData.images || []).map((img) => ({
        url: { address: img.large || img.medium || img.small },
      }));

      const prevInfo = savedHotel.hotelDescriptiveContent?.hotelInfo || {};

      const updatedHotel = {
        ...savedHotel,
        roomStays: {
          ...savedHotel.roomStays,
          roomTypes: mappedRoomTypes,
        },
        _originalData: hotelAvail,
        hotelDescriptiveContent: {
          ...savedHotel.hotelDescriptiveContent,
          hotelInfo: {
            ...prevInfo,
            ...(hotelFacilities.length > 0 && { hotelAmenities: hotelFacilities }),
            ...(hotelDescription && {
              descriptions: {
                ...prevInfo.descriptions,
                descriptiveText: hotelDescription,
                ...(detailImages.length > 0 && {
                  multimediaDescriptionsType: {
                    multimediaDescriptions: [
                      {
                        imageItemsType: { imageItems: detailImages },
                      },
                    ],
                  },
                }),
              },
            }),
            ...(checkInTime && {
              checkInHours: {
                start: checkInTime,
                end: checkOutTime,
              },
            }),
          },
        },
      };

      sessionStorage.setItem("@hotelSelected", JSON.stringify(updatedHotel));
      setHotel(updatedHotel);
      setLoadingRooms(false);
    } catch (error) {
      console.error("Error fetching hotel rooms:", error);
      setRoomError(true);
      setLoadingRooms(false);
    }
  };

  const stars = hotel
    ? Array(Math.round(hotel.basicPropertyInfo?.award?.rating))
        .fill()
        .map((_, idx) => hotel.basicPropertyInfo?.award?.rating + idx)
    : [];

  const getAvailableBoardTypes = () => {
    const roomTypes = hotel?.roomStays?.roomTypes || [];
    const boardTypes = new Map();
    roomTypes.forEach((room) => {
      const mealPlan = room.roomRates?.[0]?.mealPlan || "";
      if (mealPlan && !boardTypes.has(mealPlan)) {
        boardTypes.set(mealPlan, mealPlan);
      }
    });
    return Array.from(boardTypes.values());
  };

  const filterRoomsByBoardType = (rooms) => {
    if (boardTypeFilter === "all") return rooms;
    return rooms.filter((room) => {
      const mealPlan = room.roomRates?.[0]?.mealPlan || "";
      return mealPlan === boardTypeFilter;
    });
  };

  const availableBoardTypes = getAvailableBoardTypes();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <S.Wrapper>
      <Container>
        <Grid container>
          <Grid item xs={12} md={12} lg={8}>
            <S.CarouselContainer>
              <CarouselComponent
                buscaAtual={buscaAtual}
                images={
                  hotel?.hotelDescriptiveContent.hotelInfo.descriptions.multimediaDescriptionsType.multimediaDescriptions[0].imageItemsType?.imageItems?.map(
                    (img) => img.url.address,
                  ) || [hotel?.basicPropertyInfo.imageURL]
                }
              />
            </S.CarouselContainer>
          </Grid>
          <Grid item xs={12} md={12} lg={4}>
            <S.HotelName>
              <span>
                <S.Title>{hotel?.basicPropertyInfo.hotelRef.hotelName}</S.Title>
              </span>
              {stars.map((star, index) => (
                <GradeIcon fontSize="small" key={index} />
              ))}
              {hotel?.basicPropertyInfo.address.addressLine && (
                <span className="location">
                  <PlaceIcon fontSize="small" />
                  <S.PlaceLink
                    target="_blank"
                    rel="noreferrer"
                    href={`https://www.google.com.br/maps/place/${hotel?.basicPropertyInfo.address.addressLine.replaceAll(
                      " ",
                      "+",
                    )}`}
                  >
                    {hotel?.basicPropertyInfo.address.addressLine}
                  </S.PlaceLink>
                </span>
              )}
            </S.HotelName>
            <S.InformacoesUteis>
              <S.Title3>Informações úteis</S.Title3>
              <S.CheckInOut>
                <strong>Check-in: </strong>
                <span>
                  {hotel?.hotelDescriptiveContent?.hotelInfo?.checkInHours?.start || "Não informado"}
                </span>
              </S.CheckInOut>
              <S.CheckInOut>
                <strong>Check-out: </strong>
                <span>
                  {hotel?.hotelDescriptiveContent?.hotelInfo?.checkInHours?.end || "Não informado"}
                </span>
              </S.CheckInOut>
            </S.InformacoesUteis>
          </Grid>
          <Grid item xs={12}>
            <S.Title3>Descrição</S.Title3>
            <S.Descricao
              dangerouslySetInnerHTML={{
                __html:
                  hotel?.hotelDescriptiveContent?.hotelInfo?.descriptions
                    .descriptiveText,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            {loadingRooms ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "40px 0",
                }}
              >
                <CircularProgress />
                <span style={{ marginLeft: 12 }}>
                  Buscando quartos disponíveis...
                </span>
              </div>
            ) : roomError ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <S.Title3>Nenhum quarto disponível encontrado.</S.Title3>
              </div>
            ) : searchState?.qtRooms > 1 ? (
              <>
                {availableBoardTypes.length > 1 && (
                  <S.BoardTypeFilter>
                    <S.BoardTypeChip
                      active={boardTypeFilter === "all"}
                      onClick={() => setBoardTypeFilter("all")}
                    >
                      Todos
                    </S.BoardTypeChip>
                    {availableBoardTypes.map((bt) => (
                      <S.BoardTypeChip
                        key={bt}
                        active={boardTypeFilter === bt}
                        onClick={() => setBoardTypeFilter(bt)}
                      >
                        {bt}
                      </S.BoardTypeChip>
                    ))}
                  </S.BoardTypeFilter>
                )}
                <MultipleRoomSelector
                  searchState={searchState}
                  hotel={hotel}
                  nights={nights}
                  buscaAtual={buscaAtual}
                  boardTypeFilter={boardTypeFilter}
                />
              </>
            ) : (
              <>
                <S.Title3>Quartos disponíveis</S.Title3>
                {availableBoardTypes.length > 1 && (
                  <S.BoardTypeFilter>
                    <S.BoardTypeChip
                      active={boardTypeFilter === "all"}
                      onClick={() => setBoardTypeFilter("all")}
                    >
                      Todos
                    </S.BoardTypeChip>
                    {availableBoardTypes.map((bt) => (
                      <S.BoardTypeChip
                        key={bt}
                        active={boardTypeFilter === bt}
                        onClick={() => setBoardTypeFilter(bt)}
                      >
                        {bt}
                      </S.BoardTypeChip>
                    ))}
                  </S.BoardTypeFilter>
                )}
                {hotel?.roomStays.roomTypes &&
                  filterRoomsByBoardType([...hotel.roomStays.roomTypes])
                    .sort((a, b) => {
                      if (
                        a.roomRates[0]?.[
                          a.roomRates[0].total.currencyCode === "BRL"
                            ? "total"
                            : "origTotal" || "total"
                        ].amountAfterTax >
                        b.roomRates[0]?.[
                          b.roomRates[0].total.currencyCode === "BRL"
                            ? "total"
                            : "origTotal" || "total"
                        ].amountAfterTax
                      ) {
                        return 1;
                      }
                      if (
                        a.roomRates[0]?.[
                          a.roomRates[0].total.currencyCode === "BRL"
                            ? "total"
                            : "origTotal" || "total"
                        ].amountAfterTax <
                        b.roomRates[0]?.[
                          b.roomRates[0].total.currencyCode === "BRL"
                            ? "total"
                            : "origTotal" || "total"
                        ].amountAfterTax
                      ) {
                        return -1;
                      }
                      return 0;
                    })
                    .map((room) => {
                      return (
                        <Room
                          searchState={searchState}
                          roomName={room.roomName}
                          roomRates={room.roomRates}
                          nights={nights}
                          hotel={hotel}
                          room={room}
                          buscaAtual={buscaAtual}
                        />
                      );
                    })}
              </>
            )}
          </Grid>
          {(hotel?.basicPropertyInfo?.address?.addressLine ||
            hotel?.basicPropertyInfo?.position) && (
            <Grid item xs={12} lg={12}>
              <div>
                <iframe
                  width="100%"
                  height="450"
                  frameborder="0"
                  title="map"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAC58QTaHYJqtPPW2COx4a7f6PXNeU0DUU&q=${
                    hotel?.basicPropertyInfo?.address?.addressLine
                      ? hotel?.basicPropertyInfo?.address?.addressLine?.replaceAll(
                          " ",
                          "+",
                        )
                      : `${hotel?.basicPropertyInfo?.position?.latitude},${hotel?.basicPropertyInfo?.position?.longitude}`
                  }`}
                  allowfullscreen
                ></iframe>
              </div>
            </Grid>
          )}
          {hotel?.hotelDescriptiveContent?.hotelInfo?.hotelAmenities?.length > 0 && (
            <Grid item xs={12} lg={12}>
              <S.Title3>Comodidades do hotel</S.Title3>

              <S.Amenities>
                {hotel.hotelDescriptiveContent.hotelInfo.hotelAmenities.map(
                  (amenitie, index) => (
                    <span key={index}>
                      <CheckIcon fontSize="small" />
                      {amenitie.hotelAmenity}
                    </span>
                  ),
                )}
              </S.Amenities>
            </Grid>
          )}
        </Grid>
      </Container>
    </S.Wrapper>
  );
}

export default withRouter(HotelRoomDetails);
